import { useEffect, useRef, useState } from 'react';
import type { SearchResult } from '@mbus/shared';
import { fetchSearch } from '../api';

interface Props {
  onPick: (r: SearchResult) => void;
}

const KIND_ICON: Record<string, string> = {
  stop: '🚏',
  station: '🚉',
  place: '📍',
  address: '🏠',
  saved: '★',
};

export function SearchBar({ onPick }: Props) {
  const [text, setText] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ctrlRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    ctrlRef.current?.abort();
    const trimmed = text.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;
      try {
        const res = await fetchSearch(trimmed, ctrl.signal);
        setResults(res.results ?? []);
        setOpen(true);
      } catch {
        /* 入力中の中断・一時失敗は無視 */
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [text]);

  return (
    <div className="search-wrap">
      <input
        className="search-input"
        type="search"
        placeholder="停留所・駅・場所を検索"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        aria-label="検索"
      />
      {open && results.length > 0 && (
        <ul className="search-results" role="listbox">
          {results.slice(0, 8).map((r, i) => (
            <li key={`${r.kind}-${r.title}-${i}`}>
              <button
                className="search-result"
                onClick={() => {
                  onPick(r);
                  setOpen(false);
                  setText('');
                }}
              >
                <span className="search-icon">{KIND_ICON[r.kind] ?? '📍'}</span>
                <span className="search-main">
                  <span className="search-title">{r.title}</span>
                  {r.subtitle && <span className="search-sub">{r.subtitle}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
